import { useMemo } from 'react'
import { InstagramPost } from './InstagramPost/InstagramPost'
import { LinkedinPost } from './LinkedinPost/LinkedinPost'
import { FrameSkinProps } from './Types'
import { ASPECT_RATIOS, AspectRatioKey, DesignChannels } from 'components/NewFileModal/Types'
import { TwitterPost } from './TwitterPost/TwitterPost'
import { FacebookPost } from './FacebookPost/FacebookPost'
import { TikTokPost } from './TikTokPost/TikTokPost'
import { maxCharacters, providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import YoutubeShort from './YoutubeShort/YoutubeShort'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'

const FrameSkin = ({ width, height, channel, ...rest }: FrameSkinProps) => {
  const isScheduledPostsV3 = useIsScheduledPostsV3()
  const provider = channel ? providerFromChannelType[channel] : undefined
  const maxCharacterCount = provider ? maxCharacters[provider] : undefined
  const aspectRatioKey = useMemo(() => {
    if (!channel) return undefined

    const candidateDesignChannels = Object.entries(DesignChannels)
      .filter(([_, designChannel]) => designChannel === channel)
      .map(([aspectRatioKey]) => aspectRatioKey)

    return Object.entries(ASPECT_RATIOS).find(
      ([key, aspectRatio]) =>
        aspectRatio.width === width && aspectRatio.height === height && candidateDesignChannels.includes(key),
    )?.[0] as AspectRatioKey | undefined
  }, [width, height, channel])

  if (isScheduledPostsV3) {
    return <>{rest.children}</>
  }

  switch (aspectRatioKey) {
    case 'instagram-square-post':
    case 'instagram-portrait-post':
    case 'instagram-landscape-post':
    case 'instagram-reel':
      return <InstagramPost {...rest} maxCharacterCount={maxCharacterCount} />
    case 'facebook-post':
    case 'facebook-portrait-post':
      return <FacebookPost {...rest} maxCharacterCount={maxCharacterCount} />
    case 'tik-tok-post':
      return <TikTokPost {...rest} maxCharacterCount={maxCharacterCount} />
    case 'linkedin-post':
    case 'linkedin-portrait-post':
      return <LinkedinPost {...rest} maxCharacterCount={maxCharacterCount} />
    case 'twitter-post':
    case 'twitter-portrait-post':
      return <TwitterPost {...rest} maxCharacterCount={maxCharacterCount} />
    case 'youtube-short':
      return <YoutubeShort {...rest} maxCharacterCount={maxCharacterCount} />
    default:
      return <>{rest.children}</>
  }
}

export { FrameSkin }
